/**
 * Derived values behind the card's spending-limit panel (rendered by `configs/cardSpendingPanel.tsx`).
 *
 * Pure on purpose: no Firestore, no clock (the caller passes `nowMs`), no money formatting —
 * amounts stay integer microUSDC and are handed to `<Money micro>`, which is the only place
 * allowed to divide them.
 *
 * The model is a **rolling 30-day window enforced by Rain**. There is no period start and no
 * reset date, so nothing here computes one; spend is simply the sum of this card's `captured`
 * Payments whose `capturedAt` falls inside the trailing window.
 */
import type { QuerySpec } from "@/firestore/hooks";
import type { CardSpendingPolicy, PaymentDoc } from "@/firestore/types";
import { isMissingIndexError, isPermissionError } from "@/lib/errors";

/** Length of the only window Rain is configured with (`per30Days` → `per30DayPeriod`). */
export const SPENDING_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Backstop on how many captured payments are read for one window.
 *
 * It is not a page size the total depends on — the query's `capturedAt >=` bound is what
 * delimits the window, so this only bites if a single user genuinely has this many captures
 * inside 30 days. If it is ever reached, the total is reported as incomplete rather than
 * silently short.
 */
export const SPENDING_WINDOW_ROW_LIMIT = 1000;

/** The instant the current window opened. A plain instant comparison — no calendar, no timezone. */
export function spendingWindowStartMs(nowMs: number): number {
  return nowMs - SPENDING_WINDOW_DAYS * DAY_MS;
}

/**
 * The limit Rain enforces, in microUSDC.
 *
 * Falls back to the deprecated `monthlyLimitMicroUsdc` mirror for documents last projected
 * before the rolling-window change — the backend writes that mirror as an exact copy of the
 * limit, so the number is the same one; only the (now-deleted) monthly framing was wrong.
 */
export function policyLimitMicroUsdc(policy: CardSpendingPolicy | null | undefined): number | null {
  const limit = policy?.limitMicroUsdc ?? policy?.monthlyLimitMicroUsdc;
  return typeof limit === "number" && Number.isFinite(limit) ? limit : null;
}

/** Human label for the window. "Not recorded" on documents that predate the change. */
export function spendingWindowLabel(window: string | null | undefined): string {
  if (window === "per30Days") return `Rolling ${SPENDING_WINDOW_DAYS} days`;
  if (!window) return "Not recorded";
  return `Unrecognised (${window})`;
}

/**
 * What the deprecated compatibility mirrors are doing on this particular document.
 *
 * - `null` — no mirrors present, nothing to explain.
 * - `"mirror"` — the document carries `limitMicroUsdc`, so the mirrors are duplicates of it.
 * - `"source"` — the document predates the rolling-window change and carries *only*
 *   `monthlyLimitMicroUsdc`, so the mirror is where the limit on screen was actually read
 *   from. It cannot be described as something the raw document "also" carries.
 */
export type LegacyMirrorRole = "mirror" | "source";

export function legacyMirrorRole(
  policy: CardSpendingPolicy | null | undefined,
): LegacyMirrorRole | null {
  const hasMirrors =
    policy?.monthlyLimitMicroUsdc !== undefined || policy?.currentPeriodStart !== undefined;
  if (!hasMirrors) return null;
  const readFromMirror =
    policy?.limitMicroUsdc === undefined && policy?.monthlyLimitMicroUsdc !== undefined;
  return readFromMirror ? "source" : "mirror";
}

/**
 * Live query for the captures the window is summed from.
 *
 * This is the contract's client window query verbatim: `owner == <cardholder> AND
 * state == "captured" AND capturedAt >= windowStart ORDER BY capturedAt DESC LIMIT 1000`,
 * served by the composite index the backend adds in this same change
 * (`payments__owner_state_capturedAtDesc`). The card is filtered in memory afterwards, since a
 * card-scoped equivalent index does not exist. `capturedAt` rather than `approvedAt` on
 * purpose — the window is defined by when money was actually taken, and a payment approved
 * before the window can be captured inside it.
 *
 * `windowStart` is a `Date`, never an ISO string: the read hook forwards the value straight to
 * Firestore's `where()`, and Firestore orders by type before value, so a string bound compared
 * against a Timestamp field matches zero documents. (The hook normalises Timestamps to ISO
 * strings on the way *out*; that conversion does not run on query bounds.)
 *
 * `owner` here is the *cardholder's* uid read off the card document, not the operator's —
 * admins read every user's payments (`isPlatformAdmin()` in the wallet security rules), so
 * scoping by owner costs nothing and keeps the query on the index the mobile apps use.
 *
 * The card's own related-payments list cannot be reused for this: it pages at 50 rows, orders
 * by `approvedAt` and includes every state, so a total taken from it would be short in silence.
 */
export function capturedPaymentsQuery(owner: string, windowStart: Date): QuerySpec {
  return {
    filters: [
      ["owner", "==", owner],
      ["state", "==", "captured"],
      ["capturedAt", ">=", windowStart],
    ],
    orderBy: [["capturedAt", "desc"]],
    limit: SPENDING_WINDOW_ROW_LIMIT,
  };
}

/** Why a total must not be presented as a sum. */
export type IncompleteSpend = "pageLimit" | "unusableRow";

export interface WindowSpend {
  /** Sum of this card's captures inside the window, in microUSDC. */
  totalMicroUsdc: number;
  /** How many captures that total covers. */
  count: number;
  /** Non-null when the total is a floor rather than a sum — callers must then show no figure. */
  incomplete: IncompleteSpend | null;
}

type CapturedPayment = Pick<PaymentDoc, "card" | "capturedAt" | "amountMicroUsdc">;

/**
 * Sum one card's captures inside the window.
 *
 * `amountMicroUsdc` is overwritten with the final settled amount at capture, so summing
 * captured rows is exact — provided we add integers and never divide before the end.
 *
 * The window and card cuts are repeated here even though the query already bounds `capturedAt`:
 * the card cut has no server-side equivalent, and re-checking the bound keeps this function a
 * total function of its input rather than of a query it cannot see.
 */
export function summarizeWindowSpend(
  payments: readonly CapturedPayment[],
  options: { cardId: string; windowStartMs: number; rowLimit?: number },
): WindowSpend {
  const { cardId, windowStartMs, rowLimit = SPENDING_WINDOW_ROW_LIMIT } = options;
  let totalMicroUsdc = 0;
  let count = 0;
  let unusableRow = false;
  let oldestReadMs: number | null = null;

  for (const payment of payments) {
    const capturedMs = payment.capturedAt ? Date.parse(payment.capturedAt) : NaN;
    if (Number.isFinite(capturedMs) && (oldestReadMs === null || capturedMs < oldestReadMs)) {
      oldestReadMs = capturedMs;
    }
    // Scoped to THIS card: a replaced predecessor's captures belong to the predecessor's window.
    if (payment.card !== cardId) continue;
    // No capture time — the row can't be placed, so it might belong in the window.
    if (!Number.isFinite(capturedMs)) {
      unusableRow = true;
      continue;
    }
    if (capturedMs < windowStartMs) continue;
    if (!Number.isSafeInteger(payment.amountMicroUsdc)) {
      unusableRow = true;
      continue;
    }
    totalMicroUsdc += payment.amountMicroUsdc;
    count += 1;
  }

  // The backstop only bites if the oldest row we read is itself still inside the window: then
  // captures we never read may be inside it too, and the total is a floor, not a sum.
  const pageLimited =
    payments.length >= rowLimit && (oldestReadMs === null || oldestReadMs >= windowStartMs);

  return {
    totalMicroUsdc,
    count,
    incomplete: pageLimited ? "pageLimit" : unusableRow ? "unusableRow" : null,
  };
}

/**
 * Why there is no spend figure to show — null when there is one. An operator console is better
 * off stating the gap than printing a number that may be short, or whose basis it has just
 * said on the line above that it does not know.
 */
export function spendUnavailable(state: {
  /** The window recorded on the document, which the figure's basis has to match. */
  window: string | null | undefined;
  loading: boolean;
  error: Error | null;
  spend: WindowSpend | null;
}): string | null {
  const { window, loading, error, spend } = state;
  // Checked before the read state: a figure computed over a trailing 30 days is only about
  // this card's limit if 30 days is the window the limit is enforced over.
  if (!window) {
    return `This document was last projected before the rolling-window change, so it records no window and there is no stated basis for a ${SPENDING_WINDOW_DAYS}-day figure. The backend records one the next time this card is projected.`;
  }
  if (window !== "per30Days") {
    return `This document records a window of "${window}", which this panel cannot compute — it only sums the rolling ${SPENDING_WINDOW_DAYS}-day window.`;
  }
  if (loading) return "Loading captured payments…";
  if (error) {
    if (isMissingIndexError(error)) {
      return "The payments index this needs (owner, state, capturedAt) isn't deployed in this environment yet.";
    }
    if (isPermissionError(error)) return "You don't have access to this user's payments.";
    return error.message;
  }
  if (!spend) return "Not computed.";
  if (spend.incomplete === "pageLimit") {
    return `More than ${SPENDING_WINDOW_ROW_LIMIT.toLocaleString()} captured payments were read for this user inside the window, so any total here would be a floor rather than a sum.`;
  }
  if (spend.incomplete === "unusableRow") {
    return "At least one capture on this card has no readable capture time or amount, so a total would be short.";
  }
  return null;
}
